import { CommonModule } from '@angular/common';
import { Component, computed, effect, Injector, input, ViewChild } from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable } from 'rxjs';
import {
  DocLineRole,
  DocLineUpdatedDto,
  SupplierPreparationDto,
  SupplierPreparationService,
  SupplierPreparationStatus,
} from '@digiwall/goliath-documents/proxy';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';
import { ObserverQueue } from '@digiwall/core';
import { FieldTypes } from '@digiwall/ag-grid';
import { DocType } from '../../../base-docs/detail/doc-type';
@Component({
  selector: 'document-supplier-preparation-lines-grid',
  templateUrl: './supplier-preparation-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class SupplierPreparationLinesEditorComponent extends BaseThirdPartyDocLineEditor<SupplierPreparationDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  readonly supplierPreparation = input<SupplierPreparationDto>();
  readonly readonly = input<boolean>(false);

  protected override handlingLinesUpdatedEvent = true;

  protected articleLineRole: DocLineRole = DocLineRole.SupplierPreparationArticleLine;
  protected textLineRole: DocLineRole = DocLineRole.DocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;

  override addActionBtns = computed(() => []);

  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticlePurchasableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];

  constructor(
    localizationService: LocalizationService,
    private preparationService: SupplierPreparationService,
    injector: Injector,
    updateQueue: ObserverQueue,
  ) {
    super(localizationService, preparationService, injector, updateQueue);

    effect(() => {
      this._document = this.supplierPreparation();
      this.setLanguageCode();
    });

    effect(() => {
      this._readonly = this.readonly();
    });

    this.initColDefs();
  }

  private initColDefs(): void {
    this.columnDefs = super
      .colDefs()
      .filter(x => x.field != 'isHidden' && x.field != 'hasSubTotal');
    this.columnDefs.push(
      ...([
        {
          ...this.colDef_Quantity,
          headerName: this.localizationService.instant('Doc::QuantityToPrepare'),
          editable: params =>
            this.isCellEditable(params) &&
            this.supplierPreparation()?.supplierPreparationStatusId ===
              SupplierPreparationStatus.Draft,
        },
        {
          field: 'quantityPrepared',
          width: 100,
          headerName: this.localizationService.instant('Doc::QuantityPrepared'),
          editable: params =>
            this.isCellEditable(params) &&
            this.supplierPreparation()?.supplierPreparationStatusId ===
              SupplierPreparationStatus.Finished,
          type: FieldTypes.Number,
          cellRendererParams: {
            decimals: this.currentCompanyStore.quantityDecimals,
          },
        },
        this.colDef_ArticleSalesUnitCode(DocType.Purchase),
        this.getColdef_StockLocation(DocType.Purchase),
        this.colDef_UnitWeight,
      ] as Array<ColDef>),
    );
  }

  isChapter = line => line.lineRole == DocLineRole.DocChapter;

  private static readonly DRAFT_FIELDS = new Set(['quantity']);
  private static readonly DRAFT_OR_FINISHED_FIELDS = new Set([
    'articleLotId',
    'articleRangeValue1Id',
    'articleRangeValue2Id',
    'articleSerialId',
    'length',
    'width',
    'thickness',
  ]);
  private static readonly FINISHED_FIELDS = new Set(['quantityPrepared']);

  public override isCellEditable(params: EditableCallbackParams<any, any>): boolean {
    if (!super.isCellEditable(params)) return false;

    const status = this.supplierPreparation()?.supplierPreparationStatusId;
    const isDraft = status === SupplierPreparationStatus.Draft;
    const isFinished = status === SupplierPreparationStatus.Finished;
    const field = params.colDef.field;

    if (params.data.lineRole === this.articleLineRole) {
      if (params.data.articleId == null) return false;
      if (SupplierPreparationLinesEditorComponent.DRAFT_FIELDS.has(field)) return isDraft;
      if (SupplierPreparationLinesEditorComponent.FINISHED_FIELDS.has(field)) return isFinished;
      if (SupplierPreparationLinesEditorComponent.DRAFT_OR_FINISHED_FIELDS.has(field))
        return isDraft || isFinished;
      return false;
    }

    // Text and chapter lines: nothing is editable
    return false;
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this.readonly()) return [];

    return this.contextMenuItems(params);
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'quantityPrepared':
          observable = this.preparationService.setLineQuantityPrepared(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.quantityPrepared,
          );
          break;
        case 'unitPrice':
          observable = this.preparationService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.preparationService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.preparationService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.preparationService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid?.grid?.grid?.api?.applyTransaction({
            update: [oldData],
          });
        },
      });
  };
}
